import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { playTone, stopAll } from "@/lib/audioEngine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

export default function FrequencyBundleReceiver() {
  const { bundleIds } = useParams();
  const navigate = useNavigate();
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(-1);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBundle();
  }, [bundleIds]);

  const loadBundle = async () => {
    try {
      const ids = bundleIds.split(',');
      const giftPromises = ids.map(id => axios.get(`${API}/frequency-gifts/${id}`));
      const responses = await Promise.all(giftPromises);
      const loadedGifts = responses.map(r => r.data);
      setGifts(loadedGifts);
    } catch (e) {
      console.error(e);
      setError("Failed to load frequency bundle");
    } finally {
      setLoading(false);
    }
  };

  const playFrequency = (freq, index) => {
    const calib = Number(localStorage.getItem("calibrationCents") || 0);
    stopAll();
    playTone({ freq: freq, gain: 0.3, offsetCents: calib });
    setPlaying(true);
    setPlayingIndex(index);
    toast.success(`Playing ${freq} Hz! 🎵`);
  };

  const stopPlaying = () => {
    stopAll();
    setPlaying(false);
    setPlayingIndex(-1);
    toast.info("Stopped");
  };

  const playAllStack = () => {
    if (gifts.length === 0) return;
    
    // Play first frequency
    playFrequency(gifts[0].frequency_hz, 0);
    toast.success(`Playing ${gifts.length} frequencies in sequence! 🎵`);
    
    // TODO: Implement sequential playback with timing
  };

  const acceptAll = async () => {
    try {
      await Promise.all(gifts.map(gift => 
        axios.post(`${API}/frequency-gifts/${gift.id}/accept`)
      ));
      toast.success("All frequencies accepted! 💚");
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading frequency bundle...</p>
      </div>
    );
  }

  if (error || gifts.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        <Card className="sg-frame">
          <CardHeader>
            <CardTitle className="text-red-500">⚠️ Bundle Not Found</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              This frequency bundle is no longer available or has expired.
            </p>
            <Button onClick={() => navigate("/")} className="w-full">
              Go to HOME
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const firstGift = gifts[0];
  const frequencies = gifts.map(g => g.frequency_hz);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#a4ff2d]">
          🎵 Quantum Frequency Bundle
        </h1>
        <p className="text-sm text-muted-foreground">
          Someone sent you audio frequencies!
        </p>
      </div>

      <Card className="sg-frame">
        <CardContent className="pt-6 space-y-6">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r from-[#ff1744] to-[#a4ff2d] p-1">
              <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
                <span className="text-4xl">⚛️</span>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground">From</p>
              <p className="text-2xl font-semibold text-[#a4ff2d]">{firstGift.from_user}</p>
            </div>

            {firstGift.message && (
              <div className="p-4 bg-black/40 rounded border border-[#a4ff2d]/20">
                <p className="text-sm italic">"{firstGift.message}"</p>
              </div>
            )}

            <div className="p-4 bg-gradient-to-r from-[#ff1744]/10 to-[#a4ff2d]/10 border border-[#a4ff2d] rounded">
              <p className="text-sm font-semibold text-[#a4ff2d] mb-3">Frequency Stack ({frequencies.length})</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {frequencies.map((freq, idx) => (
                  <span key={idx} className="px-3 py-1 bg-black/60 rounded-full text-sm border border-[#a4ff2d]">
                    {freq} Hz
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={playAllStack} 
              className="w-full h-12 text-lg bg-gradient-to-r from-[#ff1744] to-[#a4ff2d]"
            >
              ▶️ Play Complete Stack
            </Button>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-muted-foreground">Or play individually:</p>
              {gifts.map((gift, idx) => (
                <div key={gift.id} className="flex gap-2">
                  {playingIndex === idx && playing ? (
                    <Button 
                      onClick={stopPlaying} 
                      variant="destructive" 
                      className="flex-1"
                    >
                      ⏹️ Stop {gift.frequency_hz} Hz
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => playFrequency(gift.frequency_hz, idx)} 
                      variant="outline" 
                      className="flex-1"
                    >
                      ▶️ Play {gift.frequency_hz} Hz
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button 
              onClick={acceptAll} 
              variant="secondary" 
              className="w-full"
            >
              💚 Accept & Save Bundle
            </Button>

            <Button onClick={() => navigate("/")} variant="outline" className="w-full">
              Go to HOME
            </Button>
          </div>

          <div className="text-xs text-center text-muted-foreground">
            <p>Bundle created: {new Date(firstGift.created_at).toLocaleString()}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="sg-frame">
        <CardHeader>
          <CardTitle className="text-sm">About Frequency Stacking</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          This bundle contains carefully selected frequencies designed to work together synergistically. 
          Each frequency targets specific aspects of your resonance, creating a comprehensive cymatic 
          effect that promotes holistic wellbeing. Use headphones for the best experience.
        </CardContent>
      </Card>
    </div>
  );
}
